import { botName } from './botIdentity';
// ═══════════════════════════════════════════════════════════════════════════
//  TRADE ANALYTICS — one source of truth for WR / PF / expectancy / maxDD /
//  streaks / equity / heatmaps. Tiles, tables aur charts SAB isi se compute
//  hote hain (canonical /api/trades/ledger rows par).
// ═══════════════════════════════════════════════════════════════════════════

export interface LedgerTrade {
  botId?: string;
  symbol?: string;
  optionSymbol?: string;
  expiry?: string;         // God RULE #20/#6: tradable expiry stamped on option rows
  expiryDate?: string;
  status: string;          // WIN | LOSS | BREAKEVEN | OPEN
  netPnl?: number;
  dateIso?: string;        // YYYY-MM-DD
  time?: string;           // HH:MM:SS
  [k: string]: unknown;
}

// EXPIRY VISIBILITY (God 2026-08-04, RULE #20/#6): render option trades as
// SYMBOL+STRIKE+TYPE [EXPIRY] across ALL fleets. Expiry is stamped on the ledger
// row for options (FUT/CASH carry none → omitted, never fabricated). YYYY-MM-DD
// is shortened to MM-DD for a compact column. One source of truth for every view.
export function optLabel(t: any): string {
  const base = String(t?.optionSymbol || t?.symbol || '');
  const exp = String(t?.expiry || t?.expiryDate || '').trim();
  if (!exp) return base;
  const short = /^\d{4}-\d{2}-\d{2}/.test(exp) ? exp.slice(5, 10) : exp;   // MM-DD
  return `${base} [${short}]`;
}

// ── FILLS, AVERAGES AND CONTRACT IDENTITY (God 2026-08-26) ─────────────────
// The Fleet Registry card and the Granular Position Manager both need "what did
// this leg buy and sell today, and on which contract". Keeping a copy in each is
// how this repo ended up with six spellings of one bot name (botIdentity.ts) —
// the same failure one layer down. They live here, with optLabel.

/** One side of a fill. `qty` is what makes the averages weighted. */
export type Fill = { side: 'BUY' | 'SELL'; price: number; qty: number };

/**
 * The fills a ledger row represents — a round trip is up to TWO of them.
 *
 * Which side each fill is on is READ from the row (`entrySide` / `exitSide`),
 * never inferred from the bot: Shool trades both ways, and a SHORT entry is a
 * SELL. Direction is only the fallback for older rows predating those fields.
 */
export function fillsOf(r: any): Fill[] {
  const out: Fill[] = [];
  const qty = Math.abs(Number(r.qty ?? r.quantity ?? 0)) || 0;
  const short = String(r.direction || '').toUpperCase() === 'SHORT';
  const eSide = String(r.entrySide || r.side || (short ? 'SELL' : 'BUY')).toUpperCase();
  const ePrice = Number(r.entryPrice ?? r.price ?? 0) || 0;
  if (qty > 0 && ePrice > 0 && (eSide === 'BUY' || eSide === 'SELL')) {
    out.push({ side: eSide as 'BUY' | 'SELL', price: ePrice, qty });
  }
  const xSide = String(r.exitSide || (short ? 'BUY' : 'SELL')).toUpperCase();
  const xPrice = Number(r.exitPrice ?? 0) || 0;
  // Only a genuinely closed leg contributes an exit fill. `openQty > 0` means the
  // position is still on, and a stale exitPrice on a partially-closed row would
  // otherwise count as if the whole thing had been sold.
  const closed = xPrice > 0 && Number(r.openQty ?? 0) <= 0;
  if (qty > 0 && closed && (xSide === 'BUY' || xSide === 'SELL')) {
    out.push({ side: xSide as 'BUY' | 'SELL', price: xPrice, qty });
  }
  return out;
}

/** Quantity-weighted average, or null when nothing of that side filled. A plain
 *  mean would let a 1-lot fill outvote a 10-lot one, which is not what "today's
 *  average price" means to anyone reading it. */
export const wavg = (sum: number, qty: number): number | null =>
  qty > 0 ? sum / qty : null;

/** Price formatting for premiums and futures alike — an option at 364.50 and
 *  GOLD at 162,840 are both prices, and rounding the premium to ₹365 throws away
 *  the paise that decide whether the fill was good. */
export const pxFmt = (v: number | null): string =>
  v == null ? '—' : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The contract a row traded, as a grouping key.
 *
 * securityId first: it is the only field carrying an EXPIRY, so it separates the
 * seven contracts that all answer to "NIFTY_50 24450 PE". Same reasoning the
 * server applies when pricing an exit (`_contract_exit_ltp`).
 */
export function contractKey(r: any): string {
  const sid = String(r.securityId || '').trim();
  if (sid) return `sid:${sid}`;
  const os = String(r.optionSymbol || '').trim().toUpperCase();
  if (os) return `os:${os}`;
  return `sym:${String(r.symbol || '?').toUpperCase()}`;
}

/** Human label for that contract — optLabel when the row carries an expiry. */
export function contractLabel(r: any): string {
  const os = String(r.optionSymbol || '').trim();
  if (os) return optLabel(r);
  const strike = Number(r.strike) || 0;
  const ot = String(r.optType || '').toUpperCase();
  const base = String(r.symbol || '?').toUpperCase();
  return strike ? `${base} ${strike} ${ot}`.trim() : base;
}

/**
 * Did this row's EXIT land on the contract its ENTRY opened?
 *
 * `exitSecurityId` / `exitOptionSymbol` are stamped by the ledger from
 * 2026-08-26 (server.cjs). Rows written before that carry neither, and `null`
 * says exactly that — "not knowable from this row" — rather than pretending the
 * check passed. A still-open row has no exit to compare, which is also `null`.
 */
export function exitContractMatches(r: any): boolean | null {
  const closed = Number(r.exitPrice ?? 0) > 0;
  if (!closed) return null;
  const xSid = String(r.exitSecurityId || '').trim();
  const eSid = String(r.securityId || '').trim();
  if (xSid && eSid) return xSid === eSid;
  const xOs = String(r.exitOptionSymbol || '').trim().toUpperCase();
  const eOs = String(r.optionSymbol || '').trim().toUpperCase();
  if (xOs && eOs) return xOs === eOs;
  return null;
}

export interface BotStats {
  botId: string;
  trades: number; wins: number; losses: number;
  winRate: number; netPnl: number;
  grossProfit: number; grossLoss: number;
  avgWin: number; avgLoss: number;
  profitFactor: number | null;   // null = no losses (∞)
  expectancy: number;
  maxDD: number;
  bestTrade: number; worstTrade: number;
  maxWinStreak: number; maxLossStreak: number;
}

const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export const closedOnly = (trades: LedgerTrade[]) =>
  trades.filter(t => ['WIN', 'LOSS', 'BREAKEVEN'].includes(String(t.status)));

/** chronological sort (dateIso + time) */
export const chrono = (trades: LedgerTrade[]) =>
  [...trades].sort((a, b) =>
    `${a.dateIso || ''} ${a.time || ''}`.localeCompare(`${b.dateIso || ''} ${b.time || ''}`));

export function computeStats(raw: LedgerTrade[], botId = 'ALL'): BotStats {
  const all = closedOnly(raw).filter(t => botId === 'ALL' || (t.botId || 'unknown') === botId);
  const t = chrono(all);
  const pnls = t.map(x => num(x.netPnl));
  const wins   = t.filter(x => x.status === 'WIN');
  const losses = t.filter(x => x.status === 'LOSS');
  const grossProfit = wins.reduce((s, x) => s + num(x.netPnl), 0);
  const grossLoss   = Math.abs(losses.reduce((s, x) => s + num(x.netPnl), 0));
  const avgWin  = wins.length   ? grossProfit / wins.length   : 0;
  const avgLoss = losses.length ? grossLoss / losses.length   : 0;
  const winRate = t.length ? wins.length / t.length : 0;
  // expectancy = WR×avgWin − LR×avgLoss (per trade ₹)
  const expectancy = winRate * avgWin - (t.length ? losses.length / t.length : 0) * avgLoss;
  // max drawdown on cumulative curve
  let cum = 0, peak = 0, maxDD = 0;
  for (const p of pnls) { cum += p; peak = Math.max(peak, cum); maxDD = Math.max(maxDD, peak - cum); }
  // streaks (time order)
  let mw = 0, ml = 0, cw = 0, cl = 0;
  for (const x of t) {
    if (x.status === 'WIN')  { cw++; cl = 0; }
    else if (x.status === 'LOSS') { cl++; cw = 0; }
    else { cw = 0; cl = 0; }
    mw = Math.max(mw, cw); ml = Math.max(ml, cl);
  }
  return {
    botId,
    trades: t.length, wins: wins.length, losses: losses.length,
    winRate: winRate * 100,
    netPnl: grossProfit - grossLoss,
    grossProfit, grossLoss, avgWin, avgLoss,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : (grossProfit > 0 ? null : 0),
    expectancy,
    maxDD,
    bestTrade: pnls.length ? Math.max(...pnls) : 0,
    worstTrade: pnls.length ? Math.min(...pnls) : 0,
    maxWinStreak: mw, maxLossStreak: ml,
  };
}

export function byBot(raw: LedgerTrade[]): BotStats[] {
  const ids = Array.from(new Set(closedOnly(raw).map(t => t.botId || 'unknown')));
  return ids.map(id => computeStats(raw, id)).sort((a, b) => b.netPnl - a.netPnl);
}

/** [{label(dateIso), net, cum}] in date order */
export function dailySeries(raw: LedgerTrade[]) {
  const m = new Map<string, number>();
  for (const t of closedOnly(raw)) {
    const d = t.dateIso || 'unknown';
    m.set(d, (m.get(d) || 0) + num(t.netPnl));
  }
  let cum = 0;
  return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, net]) => { cum += net; return { label, net, cum }; });
}

/** trade-by-trade cumulative equity */
export function equitySeries(raw: LedgerTrade[]) {
  let cum = 0;
  return chrono(closedOnly(raw)).map(t => { cum += num(t.netPnl); return cum; });
}

/** hour-of-day (9..15) → net ₹ — best trading time */
export function byHour(raw: LedgerTrade[]) {
  const m = new Map<number, number>();
  for (const t of closedOnly(raw)) {
    const h = parseInt(String(t.time || '').slice(0, 2));
    if (!Number.isFinite(h)) continue;
    m.set(h, (m.get(h) || 0) + num(t.netPnl));
  }
  return Array.from(m.entries()).sort((a, b) => a[0] - b[0]).map(([hour, net]) => ({ hour, net }));
}

/** symbol/strike → net ₹, top winners + losers */
export function bySymbol(raw: LedgerTrade[], topN = 5) {
  const m = new Map<string, number>();
  for (const t of closedOnly(raw)) {
    const s = String(t.optionSymbol || t.symbol || '?');
    m.set(s, (m.get(s) || 0) + num(t.netPnl));
  }
  const rows = Array.from(m.entries()).map(([symbol, net]) => ({ symbol, net }));
  return {
    winners: rows.filter(r => r.net > 0).sort((a, b) => b.net - a.net).slice(0, topN),
    losers:  rows.filter(r => r.net < 0).sort((a, b) => a.net - b.net).slice(0, topN),
  };
}

// God 2026-08-24: bot × symbol × TIMEFRAME. bySymbol() above answers "which
// strike made money"; it cannot answer "which LEG made money", because one
// symbol runs several Pine strategies at once (1s STACK200, 5s CROSS550,
// 15s/5m TRAP-513) and their P&L cancels inside a single symbol figure.
// A trade with no stamped TF becomes "(no TF)" rather than joining a real leg —
// an unattributable trade is a defect, not a rounding detail.
const TF_ORDER = ['1s', '5s', '15s', '30s', '1m', '3m', '5m', '15m', '30m', '1h', '1H', '4h', '4H', '1d', '1D', '1W'];
export const tfRank = (tf: string) => { const i = TF_ORDER.indexOf(String(tf)); return i < 0 ? 999 : i; };

export function bySymbolTf(raw: LedgerTrade[]) {
  const m = new Map<string, { bot: string; symbol: string; tf: string; trades: number; wins: number; losses: number; net: number }>();
  for (const t of closedOnly(raw)) {
    const bot = String((t as any).botId || '?');
    const symbol = String(t.symbol || '?');
    const tf = String((t as any).timeframe || '').trim() || '(no TF)';
    const k = `${bot}|${symbol}|${tf}`;
    const r = m.get(k) || { bot, symbol, tf, trades: 0, wins: 0, losses: 0, net: 0 };
    r.trades += 1;
    const st = String(t.status || '').toUpperCase();
    if (st === 'WIN') r.wins += 1; else if (st === 'LOSS') r.losses += 1;
    r.net += num(t.netPnl);
    m.set(k, r);
  }
  return Array.from(m.values()).sort((a, b) =>
    a.bot.localeCompare(b.bot) || a.symbol.localeCompare(b.symbol) || tfRank(a.tf) - tfRank(b.tf));
}

// God 2026-08-24: one name per bot, system-wide. This used to upper-case the raw
// id, so mai_veg_mcx read "MAI VEG MCX" here while the same bot was "Rudra Vega
// MCX" in Mongo and "VEGA MCX" on the fleet card. Now every caller resolves
// through the single registry in botIdentity.ts.
export const botLabel = (b: string) => botName(b);

/** Signal origin — explicit source field first, else id-shape heuristic:
 *  numeric/epoch ids are TradingView-created bots; named bots are engine-side. */
export const botSource = (botId: string, source?: string) => {
  const s = String(source || '').toUpperCase();
  if (s.includes('SUPER') || s.includes('CHART') || s.includes('MAMTA')) return 'SUPERCHART';
  if (s.includes('TV') || s.includes('TRADING')) return 'TRADINGVIEW';
  const r = String(botId || '').replace(/^bot_/, '');
  return /^\d{6,}$/.test(r) ? 'TRADINGVIEW' : 'SUPERCHART';
};

const inr = (n: number) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;

/** 3–5 data-driven plain-language insights */
export function insights(raw: LedgerTrade[]): string[] {
  const t = closedOnly(raw);
  if (t.length < 5) return ['Abhi kam trades hain — insights ke liye thoda aur data chahiye.'];
  const out: string[] = [];
  const bots = byBot(raw).filter(b => b.trades >= 3);
  if (bots.length) {
    const best = bots[0];
    out.push(`🏆 Best bot: ${botLabel(best.botId)} (+${inr(best.netPnl)}, ${best.winRate.toFixed(0)}% WR, ${best.trades} trades)`);
    const bad = bots.filter(b => b.expectancy < 0 && b.trades >= 5).sort((a, b) => a.expectancy - b.expectancy)[0];
    if (bad) out.push(`⚠ ${botLabel(bad.botId)}: ${bad.winRate.toFixed(0)}% WR aur NEGATIVE expectancy (${bad.expectancy >= 0 ? '+' : '−'}${inr(bad.expectancy)}/trade) — band karne par vichar karo`);
  }
  const hours = byHour(raw);
  if (hours.length >= 2) {
    const bestH = [...hours].sort((a, b) => b.net - a.net)[0];
    const worstH = [...hours].sort((a, b) => a.net - b.net)[0];
    if (bestH.net > 0) out.push(`⏰ Sabse profitable time: ${bestH.hour}:00–${bestH.hour + 1}:00 (+${inr(bestH.net)})`);
    if (worstH.net < 0) out.push(`🚫 Sabse weak time: ${worstH.hour}:00–${worstH.hour + 1}:00 (−${inr(worstH.net)})`);
  }
  // current streak
  const ch = chrono(t);
  let streak = 0; const lastStatus = ch[ch.length - 1]?.status;
  for (let i = ch.length - 1; i >= 0 && ch[i].status === lastStatus; i--) streak++;
  if (lastStatus === 'WIN' || lastStatus === 'LOSS') {
    out.push(`${lastStatus === 'WIN' ? '🔥' : '🧊'} Current streak: ${streak} lagatar ${lastStatus === 'WIN' ? 'wins' : 'losses'}`);
  }
  const sym = bySymbol(raw, 1);
  if (sym.winners[0]) out.push(`💎 Top strike: ${sym.winners[0].symbol} (+${inr(sym.winners[0].net)})`);
  return out.slice(0, 5);
}
